import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system';
import { getYoutubeUploadJsonApiBase, getYoutubeUploadChunkApiBase, getMobileWebUploadApiBase, buildApiUrl } from './apiConfig';
import { formatYoutubeUploadError } from './youtubeBinaryUpload';
import { videoTooLargeMessage } from './videoUploadLimits';
import { uploadNativeUriToYoutubeResumable, uploadWebFileToYoutubeResumable } from './youtubeResumableUpload';
import {
  YOUTUBE_UPLOAD_METHODS,
  formatBytes,
  isMobileWeb,
  logYoutubeUploadError,
  logYoutubeUploadPhase,
  logYoutubeUploadStart,
  logYoutubeUploadSuccess,
  safeUriHint,
} from './youtubeUploadDebug';

/** Chunk size (multiple of 256 KB). Smaller on mobile — chunks go as base64 JSON via /api. */
const DESKTOP_CHUNK_BYTES = 768 * 1024;
const MOBILE_CHUNK_BYTES = 384 * 1024;
const CHUNK_UPLOAD_TIMEOUT_MS = 120000;
const COMPLETE_UPLOAD_TIMEOUT_MS = 30 * 60 * 1000;
const MAX_CHUNK_RETRIES = 3;

function getChunkBytes() {
  if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
    if (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '')) {
      return MOBILE_CHUNK_BYTES;
    }
  }
  if (Platform.OS !== 'web') {
    return MOBILE_CHUNK_BYTES;
  }
  return DESKTOP_CHUNK_BYTES;
}

function isRawNetworkFailure(err) {
  const msg = err?.message || String(err || '');
  return (
    /^(failed to fetch|load failed|network error|network request failed)$/i.test(msg.trim()) ||
    (/^typeerror:/i.test(msg) && /fetch|network|load failed/i.test(msg))
  );
}

function toUploadError(err, fallback) {
  if (isRawNetworkFailure(err)) {
    return new Error(fallback);
  }
  return err instanceof Error ? err : new Error(fallback);
}

async function parseJsonText(text) {
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    const snippet = text.replace(/\s+/g, ' ').trim().slice(0, 160);
    return snippet ? { message: snippet } : {};
  }
}

function postJson(url, body, timeoutMs = 60000) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: controller?.signal,
  })
    .then(async (response) => ({
      status: response.status,
      text: await response.text(),
    }))
    .catch((err) => {
      if (err?.name === 'AbortError') {
        throw new Error('Request timed out');
      }
      throw err;
    })
    .finally(() => {
      if (timer) clearTimeout(timer);
    });
}

async function postJsonWithRetry(url, body, timeoutMs) {
  let lastErr;
  for (let attempt = 0; attempt < MAX_CHUNK_RETRIES; attempt += 1) {
    try {
      return await postJson(url, body, timeoutMs);
    } catch (err) {
      lastErr = err;
      if (!isRawNetworkFailure(err) || attempt === MAX_CHUNK_RETRIES - 1) break;
      await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    }
  }
  throw lastErr;
}

function uint8ArrayToBase64(bytes) {
  const block = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += block) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + block));
  }
  return btoa(binary);
}

function readBlobAsArrayBuffer(blob) {
  if (!blob) {
    return Promise.reject(new Error('Could not read the selected video. Please choose it again.'));
  }
  if (typeof FileReader !== 'undefined') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () =>
        reject(reader.error || new Error('Could not read the selected video. Please choose it again.'));
      reader.onabort = () => reject(new Error('Could not read the selected video. Please choose it again.'));
      reader.readAsArrayBuffer(blob);
    });
  }
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer();
  }
  return Promise.reject(new Error('Could not read the selected video. Please choose it again.'));
}

/**
 * Android Chrome often exposes File.size === 0 and empty slice().arrayBuffer() results.
 * Read the full file into memory once via FileReader so chunked upload has a reliable Blob.
 */
export async function materializeWebVideoFile(file, method = 'web-materialize') {
  const name = file?.name || 'video.mp4';
  const type = file?.type || 'video/mp4';

  logYoutubeUploadPhase(method, 'materialize-start', {
    reportedSize: file?.size,
    fileName: name,
    transport: 'FileReader.readAsArrayBuffer',
  });

  let buffer;
  try {
    buffer = await readBlobAsArrayBuffer(file);
  } catch (err) {
    logYoutubeUploadError(method, 'materialize-filereader', err, { fileName: name });
    const objectUrl = URL.createObjectURL(file);
    try {
      const resp = await fetch(objectUrl);
      buffer = await resp.arrayBuffer();
    } catch (fetchErr) {
      logYoutubeUploadError(method, 'materialize-fetch', fetchErr, { fileName: name });
      throw new Error('Could not read the selected video. Please choose it again.');
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  if (!buffer?.byteLength) {
    throw new Error('Could not read the selected video. Please choose it again.');
  }

  const tooLarge = videoTooLargeMessage(buffer.byteLength);
  if (tooLarge) {
    throw new Error(tooLarge);
  }

  const materialized = new File([buffer], name, {
    type,
    lastModified: file?.lastModified || Date.now(),
  });
  materialized._materializedForUpload = true;

  logYoutubeUploadPhase(method, 'materialize-complete', {
    reportedSize: file?.size,
    materializedSize: materialized.size,
    materializedSizeLabel: formatBytes(materialized.size),
  });

  return materialized;
}

/** On mobile web, copy the picked File into a readable in-memory File before upload. */
export async function prepareWebVideoFileForUpload(file, method = 'web-prepare') {
  if (!file || Platform.OS !== 'web' || !isMobileWeb()) {
    return file;
  }
  if (file._materializedForUpload && file.size > 0) {
    return file;
  }
  return materializeWebVideoFile(file, method);
}

async function readWebFileSlice(file, start, end, method) {
  const slice = file.slice(start, end);
  if (Platform.OS === 'web' && isMobileWeb()) {
    try {
      const buf = await readBlobAsArrayBuffer(slice);
      return new Uint8Array(buf);
    } catch (err) {
      logYoutubeUploadError(method, 'slice-filereader', err, { start, end });
    }
  }
  return new Uint8Array(await slice.arrayBuffer());
}

/**
 * Read a web File via slice()+arrayBuffer() — reliable on Android Chrome where
 * File.stream() often throws TypeError: network error before the first chunk.
 */
async function readWebFileInSliceChunks(file, onChunk, opts = {}) {
  const chunkBytes = opts.chunkBytes || getChunkBytes();
  const totalHint = opts.totalHint > 0 ? opts.totalHint : undefined;
  const method = opts.method || 'web-slice-read';
  let offset = 0;
  let chunkIndex = 0;

  logYoutubeUploadPhase(method, 'slice-read-start', {
    chunkBytes,
    totalHint,
    fileSize: file.size,
    materialized: Boolean(file?._materializedForUpload),
    readMode: totalHint ? 'fixed-length' : 'until-empty',
  });

  while (true) {
    if (totalHint != null && offset >= totalHint) break;
    const sliceEnd = totalHint != null ? Math.min(offset + chunkBytes, totalHint) : offset + chunkBytes;
    let buf;
    try {
      buf = await readWebFileSlice(file, offset, sliceEnd, method);
    } catch (err) {
      logYoutubeUploadError(method, 'slice-read', err, { offset, chunkIndex });
      throw toUploadError(
        err,
        'Could not read the selected video. Please choose it again and retry.'
      );
    }
    if (!buf.length) break;

    chunkIndex += 1;
    if (chunkIndex === 1 || chunkIndex % 10 === 0) {
      logYoutubeUploadPhase(method, 'slice-read-progress', {
        chunkIndex,
        offset,
        chunkBytes: buf.length,
      });
    }
    await onChunk(buf, offset, chunkIndex);
    offset += buf.length;
    opts.onProgress?.(offset, totalHint || offset);
    if (buf.length < chunkBytes) break;
  }

  if (!offset) {
    throw new Error('Could not read the selected video. Please choose it again.');
  }
  logYoutubeUploadPhase(method, 'slice-read-complete', { bytesRead: offset, chunksRead: chunkIndex });
  return offset;
}

async function postBinaryWithRetry(url, offset, body, timeoutMs = CHUNK_UPLOAD_TIMEOUT_MS) {
  let lastErr;
  for (let attempt = 0; attempt < MAX_CHUNK_RETRIES; attempt += 1) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-Upload-Offset': String(offset),
        },
        body,
        signal: controller?.signal,
      });
      const text = await response.text();
      return { status: response.status, text };
    } catch (err) {
      lastErr = err?.name === 'AbortError' ? new Error('Request timed out') : err;
      if (!isRawNetworkFailure(lastErr) || attempt === MAX_CHUNK_RETRIES - 1) break;
      await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    } finally {
      if (timer) clearTimeout(timer);
    }
  }
  throw lastErr;
}

function resolveWebFileByteLength(webFile, fileSizeHint) {
  if (fileSizeHint > 0) return fileSizeHint;
  if (webFile?.size > 0) return webFile.size;
  return 0;
}

function ensureVideoFileExtension(name) {
  const base = String(name || 'video').trim() || 'video';
  return /\.[a-z0-9]{2,5}$/i.test(base) ? base : `${base}.mp4`;
}

export async function resolveWebVideoForUpload({
  webFile,
  webBlobUri,
  fileName,
  mimeType,
  method = 'web-resolve-video',
}) {
  if (webFile?._materializedForUpload && webFile.size > 0) {
    logYoutubeUploadPhase(method, 'resolve-file', {
      pickSource: 'file-input-materialized',
      fileSize: webFile.size,
    });
    return webFile;
  }
  if (webFile?.size > 0) {
    logYoutubeUploadPhase(method, 'resolve-file', { pickSource: 'file-input', fileSize: webFile.size });
    return webFile;
  }

  if (webBlobUri && String(webBlobUri).startsWith('blob:')) {
    logYoutubeUploadPhase(method, 'resolve-blob-uri', {
      pickSource: webBlobUri.includes('blob:') ? 'file-input-blob-uri' : 'blob-uri',
      uriPrefix: String(webBlobUri).slice(0, 40),
    });
    let blob;
    try {
      const resp = await fetch(webBlobUri);
      blob = await resp.blob();
    } catch (err) {
      logYoutubeUploadError(method, 'resolve-blob-uri', err, { webBlobUri: String(webBlobUri).slice(0, 40) });
      throw new Error('Could not read the selected video. Please choose it again.');
    }
    if (!blob?.size) {
      throw new Error('Could not read the selected video. Please choose it again.');
    }
    const tooLarge = videoTooLargeMessage(blob.size);
    if (tooLarge) throw new Error(tooLarge);
    const safeName = ensureVideoFileExtension(fileName || 'video.mp4');
    const file = new File([blob], safeName, { type: mimeType || blob.type || 'video/mp4' });
    logYoutubeUploadPhase(method, 'resolve-blob-uri-ok', {
      fileSize: file.size,
      fileSizeLabel: formatBytes(file.size),
    });
    return file;
  }

  logYoutubeUploadError(method, 'resolve-unreadable', new Error('Video file is unreadable'), {
    pickSource: 'file-input',
    reportedSize: webFile?.size,
    hasBlobUri: Boolean(webBlobUri),
  });
  throw new Error(
    'Could not read the selected video. Try choosing it again, saving it as .mp4 on your device first, or paste a YouTube URL instead.'
  );
}

/**
 * Start FileReader in the same synchronous turn as the file-input change event.
 * Android invalidates handles after async gaps; do not await before calling this.
 */
export function startMobileWebVideoMaterialize(file, { onReady, onError, method = 'web-pick-sync-read' } = {}) {
  logYoutubeUploadPhase(method, 'sync-read-start', {
    reportedSize: file?.size,
    fileName: file?.name,
  });
  const reader = new FileReader();
  reader.onload = () => {
    const buffer = reader.result;
    if (!buffer?.byteLength) {
      onError?.(new Error('Could not read the selected video. Please choose it again.'));
      return;
    }
    const tooLarge = videoTooLargeMessage(buffer.byteLength);
    if (tooLarge) {
      onError?.(new Error(tooLarge));
      return;
    }
    const mat = new File([buffer], ensureVideoFileExtension(file.name), {
      type: file.type || 'video/mp4',
    });
    mat._materializedForUpload = true;
    logYoutubeUploadPhase(method, 'sync-read-complete', {
      materializedSize: mat.size,
      materializedSizeLabel: formatBytes(mat.size),
    });
    onReady?.(mat);
  };
  reader.onerror = () => {
    logYoutubeUploadError(method, 'sync-read', reader.error, { fileName: file?.name });
    onError?.(reader.error || new Error('Could not read the selected video. Please choose it again.'));
  };
  try {
    reader.readAsArrayBuffer(file);
  } catch (err) {
    logYoutubeUploadError(method, 'sync-read-throw', err, { fileName: file?.name });
    onError?.(err);
  }
}

function selectWebUploadMethod(_webFile, _fileSizeHint) {
  if (isMobileWeb()) {
    return YOUTUBE_UPLOAD_METHODS.WEB_MOBILE_XHR;
  }
  return YOUTUBE_UPLOAD_METHODS.WEB_SESSION_B64;
}

function xhrPostBinary(url, offset, body, timeoutMs = CHUNK_UPLOAD_TIMEOUT_MS) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.setRequestHeader('X-Upload-Offset', String(offset));
    xhr.timeout = timeoutMs;
    xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText });
    xhr.onerror = () => reject(new TypeError('network error'));
    xhr.ontimeout = () => reject(new Error('Request timed out'));
    xhr.send(body);
  });
}

async function postBinaryViaXHRWithRetry(url, offset, body, timeoutMs, method, chunkIndex) {
  let lastErr;
  for (let attempt = 0; attempt < MAX_CHUNK_RETRIES; attempt += 1) {
    try {
      if (attempt > 0 || chunkIndex === 1) {
        logYoutubeUploadPhase(method, 'xhr-chunk', {
          chunkIndex,
          attempt: attempt + 1,
          offset,
          bodySize: body?.size,
          transport: 'XMLHttpRequest.send(Blob)',
        });
      }
      return await xhrPostBinary(url, offset, body, timeoutMs);
    } catch (err) {
      lastErr = err;
      logYoutubeUploadError(method, 'xhr-chunk', err, {
        chunkIndex,
        attempt: attempt + 1,
        offset,
        bodySize: body?.size,
      });
      if (!isRawNetworkFailure(err) || attempt === MAX_CHUNK_RETRIES - 1) break;
      await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    }
  }
  throw toUploadError(
    lastErr,
    'Lost connection while uploading the video. Check your network and try again.'
  );
}

function xhrPostMultipart(url, formData, timeoutMs, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.timeout = timeoutMs;
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress({ loaded: event.loaded, total: event.total });
        }
      };
    }
    xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText });
    xhr.onerror = () => reject(new TypeError('network error'));
    xhr.ontimeout = () => reject(new Error('Video upload timed out'));
    xhr.send(formData);
  });
}

async function uploadWebFileViaMultipartXHR({
  method,
  api,
  webFile,
  fileName,
  title,
  description,
  privacyStatus,
  isShort,
  account_key,
  onProgress,
}) {
  const uploadUrl = buildApiUrl(api, '/api/youtube/upload-file');
  logYoutubeUploadPhase(method, 'multipart-xhr-start', {
    uploadUrl,
    apiBase: api || '(same-origin)',
    fileName,
    reportedSize: webFile?.size,
    transport: 'XMLHttpRequest FormData',
  });

  let lastErr;
  for (let attempt = 0; attempt < MAX_CHUNK_RETRIES; attempt += 1) {
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('privacyStatus', privacyStatus);
      formData.append('isShort', isShort ? 'true' : 'false');
      formData.append('account_key', account_key);
      formData.append('video', webFile, ensureVideoFileExtension(fileName || webFile?.name));

      const result = await xhrPostMultipart(
        uploadUrl,
        formData,
        COMPLETE_UPLOAD_TIMEOUT_MS,
        onProgress
      );
      const data = await parseJsonText(result.text);
      if (result.status < 200 || result.status >= 300) {
        throw new Error(formatYoutubeUploadError(data, 'YouTube upload failed', result.status));
      }
      const youtubeUrl = data.youtubeUrl;
      if (!youtubeUrl) {
        throw new Error('No YouTube URL returned from server');
      }
      logYoutubeUploadSuccess(method, { youtubeUrl: String(youtubeUrl).trim(), transport: 'multipart-xhr' });
      return String(youtubeUrl).trim();
    } catch (err) {
      lastErr = err;
      logYoutubeUploadError(method, 'multipart-xhr', err, { attempt: attempt + 1, uploadUrl });
      if (!isRawNetworkFailure(err) || attempt === MAX_CHUNK_RETRIES - 1) break;
      await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    }
  }
  throw toUploadError(
    lastErr,
    'Lost connection while uploading the video. Check your network and try again.'
  );
}

async function uploadWebFileViaMobileXHR({
  webFile,
  webBlobUri,
  fileName,
  mimeType,
  title,
  description = '',
  privacyStatus = 'unlisted',
  isShort = false,
  account_key = 'default',
  onProgress,
}) {
  const method = YOUTUBE_UPLOAD_METHODS.WEB_MOBILE_XHR;
  const api = getMobileWebUploadApiBase();

  const resolvedFile = await resolveWebVideoForUpload({
    webFile,
    webBlobUri,
    fileName,
    mimeType,
    method,
  });
  webFile = resolvedFile;
  const chunkBytes = getChunkBytes();
  const safeFileName = ensureVideoFileExtension(fileName || webFile?.name);

  logYoutubeUploadStart(method, {
    fileName: safeFileName,
    mimeType,
    reportedSize: webFile?.size,
    reportedSizeLabel: formatBytes(webFile?.size),
    chunkBytes,
    apiBase: api || '(same-origin)',
    transport: 'same-origin XHR multipart → session chunk fallback',
  });

  await wakeUploadBackend(api, method);

  const probeSlice = webFile.slice(0, Math.min(chunkBytes, 256 * 1024));
  if (probeSlice.size === 0) {
    logYoutubeUploadPhase(method, 'slice-probe-empty-multipart', {
      reportedSize: webFile?.size,
      transport: 'XMLHttpRequest FormData (direct)',
    });
    return uploadWebFileViaMultipartXHR({
      method,
      api,
      webFile,
      fileName: safeFileName,
      title,
      description,
      privacyStatus,
      isShort,
      account_key,
      onProgress,
    });
  }

  const startUrl = buildApiUrl(api, '/api/youtube/upload-session/start');
  let startRes;
  try {
    startRes = await postJsonWithRetry(startUrl, { mimeType, fileName: safeFileName }, 60000);
  } catch (err) {
    logYoutubeUploadError(method, 'session-start', err, { startUrl });
    throw toUploadError(
      err,
      'Could not reach the server to start the video upload. Check your connection and try again.'
    );
  }

  const startData = await parseJsonText(startRes.text);
  if (startRes.status < 200 || startRes.status >= 300 || !startData.sessionId) {
    throw new Error(
      formatYoutubeUploadError(startData, 'Could not start video upload', startRes.status)
    );
  }

  const { sessionId } = startData;
  logYoutubeUploadPhase(method, 'session-started', { sessionId });

  let offset = 0;
  let chunkIndex = 0;
  try {
    while (true) {
      const blob = webFile.slice(offset, offset + chunkBytes);
      if (blob.size === 0) {
        if (offset === 0) {
          logYoutubeUploadPhase(method, 'xhr-slice-empty-multipart-fallback', { sessionId });
          try {
            await postJson(buildApiUrl(api, `/api/youtube/upload-session/${sessionId}/cancel`), {}, 15000);
          } catch {
            /* ignore */
          }
          return uploadWebFileViaMultipartXHR({
            method,
            api,
            webFile,
            fileName: safeFileName,
            title,
            description,
            privacyStatus,
            isShort,
            account_key,
            onProgress,
          });
        }
        break;
      }

      chunkIndex += 1;
      const chunkUrl = buildApiUrl(api, `/api/youtube/upload-session/${sessionId}/chunk`);
      const chunkRes = await postBinaryViaXHRWithRetry(
        chunkUrl,
        offset,
        blob,
        CHUNK_UPLOAD_TIMEOUT_MS,
        method,
        chunkIndex
      );
      const chunkData = await parseJsonText(chunkRes.text);
      if (chunkRes.status < 200 || chunkRes.status >= 300) {
        throw new Error(
          formatYoutubeUploadError(chunkData, 'Video upload failed', chunkRes.status)
        );
      }

      offset += blob.size;
      onProgress?.({ loaded: offset, total: webFile.size > 0 ? webFile.size : offset });
      if (blob.size < chunkBytes) break;
    }

    logYoutubeUploadPhase(method, 'session-complete', { sessionId, bytesUploaded: offset, chunksSent: chunkIndex });
    const completeRes = await postJsonWithRetry(
      buildApiUrl(api, `/api/youtube/upload-session/${sessionId}/complete`),
      { title, description, privacyStatus, isShort, account_key },
      COMPLETE_UPLOAD_TIMEOUT_MS
    );
    const completeData = await parseJsonText(completeRes.text);
    if (completeRes.status < 200 || completeRes.status >= 300) {
      throw new Error(
        formatYoutubeUploadError(completeData, 'YouTube upload failed', completeRes.status)
      );
    }
    const youtubeUrl = completeData.youtubeUrl;
    if (!youtubeUrl) throw new Error('No YouTube URL returned from server');
    logYoutubeUploadSuccess(method, { sessionId, youtubeUrl: String(youtubeUrl).trim(), chunksSent: chunkIndex });
    return String(youtubeUrl).trim();
  } catch (error) {
    try {
      await postJson(buildApiUrl(api, `/api/youtube/upload-session/${sessionId}/cancel`), {}, 15000);
    } catch {
      /* ignore */
    }
    logYoutubeUploadError(method, 'upload-failed', error, {
      fileName,
      chunksSent: chunkIndex,
      bytesUploaded: offset,
    });
    throw error;
  }
}

async function streamFileInChunks(file, onChunk, opts = {}) {
  if (Platform.OS === 'web' && isMobileWeb()) {
    return readWebFileInSliceChunks(file, onChunk, opts);
  }

  const chunkBytes = getChunkBytes();
  const totalHint = opts.totalHint > 0 ? opts.totalHint : undefined;

  if (typeof file.stream !== 'function') {
    const total = file.size > 0 ? file.size : totalHint;
    if (!total) {
      throw new Error('Could not read the selected video. Please choose it again.');
    }
    let offset = 0;
    while (offset < total) {
      const end = Math.min(offset + chunkBytes, total);
      const slice = file.slice(offset, end);
      const buf = new Uint8Array(await slice.arrayBuffer());
      if (!buf.length) {
        throw new Error('Could not read the selected video. Please choose it again.');
      }
      await onChunk(buf, offset);
      offset += buf.length;
      opts.onProgress?.(offset, total);
      if (buf.length < chunkBytes) break;
    }
    return offset;
  }

  const reader = file.stream().getReader();
  let pending = new Uint8Array(0);
  let offset = 0;

  while (true) {
    while (pending.length < chunkBytes) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value?.byteLength) continue;
      const merged = new Uint8Array(pending.length + value.byteLength);
      merged.set(pending);
      merged.set(value, pending.length);
      pending = merged;
    }
    if (!pending.length) break;

    const toSend = pending.slice(0, Math.min(chunkBytes, pending.length));
    pending = pending.slice(toSend.length);
    await onChunk(toSend, offset);
    offset += toSend.length;
    opts.onProgress?.(offset, totalHint);
    if (toSend.length < chunkBytes) break;
  }

  if (!offset) {
    throw new Error('Could not read the selected video. Please choose it again.');
  }
  return offset;
}

async function wakeUploadBackend(jsonApiBase, method) {
  const healthUrl = buildApiUrl(jsonApiBase, '/api/health');
  logYoutubeUploadPhase(method, 'wake-backend', { healthUrl });
  try {
    const res = await fetch(healthUrl, { method: 'GET', cache: 'no-store' });
    logYoutubeUploadPhase(method, 'wake-backend-ok', {
      healthUrl,
      status: res.status,
    });
  } catch (err) {
    logYoutubeUploadPhase(method, 'wake-backend-failed', {
      healthUrl,
      errorMessage: err?.message || String(err),
    });
  }
}

/**
 * Copy content:// / ph:// URIs to a cache file so expo-file-system can read byte ranges.
 * @returns {{ uri: string, cleanup: () => Promise<void> }}
 */
async function resolveNativeVideoUri(uri) {
  const raw = String(uri || '').trim();
  if (!raw) {
    throw new Error('Could not read the selected video. Please choose it again.');
  }
  if (raw.startsWith('file://')) {
    return { uri: raw, cleanup: async () => {} };
  }
  const dest = `${FileSystem.cacheDirectory}yt_upload_${Date.now()}.mp4`;
  await FileSystem.copyAsync({ from: raw, to: dest });
  return {
    uri: dest,
    cleanup: async () => {
      try {
        await FileSystem.deleteAsync(dest, { idempotent: true });
      } catch {
        /* ignore */
      }
    },
  };
}

async function getNativeVideoByteLength(readUri, fileSizeHint) {
  if (fileSizeHint > 0) return fileSizeHint;
  const info = await FileSystem.getInfoAsync(readUri, { size: true });
  if (info.exists && typeof info.size === 'number' && info.size > 0) {
    return info.size;
  }
  throw new Error('Could not read the selected video. Please choose it again.');
}

async function runServerSessionUpload({
  method,
  api,
  fileName,
  mimeType,
  title,
  description,
  privacyStatus,
  isShort,
  account_key,
  uploadAllChunks,
}) {
  await wakeUploadBackend(api, method);

  const startUrl = buildApiUrl(api, '/api/youtube/upload-session/start');
  logYoutubeUploadPhase(method, 'session-start', {
    startUrl,
    fileName,
    mimeType,
    chunkBytes: getChunkBytes(),
    transport: 'fetch-json-b64',
  });

  let startRes;
  try {
    startRes = await postJsonWithRetry(
      startUrl,
      { mimeType, fileName },
      60000
    );
  } catch (err) {
    logYoutubeUploadError(method, 'session-start', err, { startUrl });
    throw toUploadError(
      err,
      'Could not reach the server to start the video upload. Check your connection and try again.'
    );
  }

  const startData = await parseJsonText(startRes.text);
  if (startRes.status < 200 || startRes.status >= 300 || !startData.sessionId) {
    throw new Error(
      formatYoutubeUploadError(startData, 'Could not start video upload', startRes.status)
    );
  }

  const { sessionId } = startData;
  logYoutubeUploadPhase(method, 'session-started', { sessionId });

  try {
    await uploadAllChunks(async (chunkPayload, offset) => {
      const chunkUrl = buildApiUrl(api, `/api/youtube/upload-session/${sessionId}/chunk-b64`);
      let chunkRes;
      try {
        chunkRes = await postJsonWithRetry(
          chunkUrl,
          { offset, data: chunkPayload },
          CHUNK_UPLOAD_TIMEOUT_MS
        );
      } catch (err) {
        logYoutubeUploadError(method, 'session-chunk', err, {
          chunkUrl,
          offset,
          payloadChars: chunkPayload?.length,
        });
        throw toUploadError(
          err,
          'Lost connection while uploading the video. Check your network and try again.'
        );
      }
      const chunkData = await parseJsonText(chunkRes.text);
      if (chunkRes.status < 200 || chunkRes.status >= 300) {
        const err = new Error(
          formatYoutubeUploadError(chunkData, 'Video upload failed', chunkRes.status)
        );
        logYoutubeUploadError(method, 'session-chunk-http', err, {
          chunkUrl,
          offset,
          httpStatus: chunkRes.status,
        });
        throw err;
      }
    });

    logYoutubeUploadPhase(method, 'session-complete', { sessionId });
    let completeRes;
    try {
      completeRes = await postJsonWithRetry(
        buildApiUrl(api, `/api/youtube/upload-session/${sessionId}/complete`),
        {
          title,
          description,
          privacyStatus,
          isShort,
          account_key,
        },
        COMPLETE_UPLOAD_TIMEOUT_MS
      );
    } catch (err) {
      logYoutubeUploadError(method, 'session-complete', err, { sessionId });
      throw toUploadError(
        err,
        'Lost connection while finishing the video upload. Check your network and try again.'
      );
    }

    const completeData = await parseJsonText(completeRes.text);
    if (completeRes.status < 200 || completeRes.status >= 300) {
      const err = new Error(
        formatYoutubeUploadError(completeData, 'YouTube upload failed', completeRes.status)
      );
      logYoutubeUploadError(method, 'session-complete-http', err, {
        sessionId,
        httpStatus: completeRes.status,
      });
      throw err;
    }
    const y = completeData.youtubeUrl;
    if (!y) throw new Error('No YouTube URL returned from server');
    logYoutubeUploadSuccess(method, { sessionId, youtubeUrl: String(y).trim() });
    return String(y).trim();
  } catch (error) {
    try {
      await postJson(buildApiUrl(api, `/api/youtube/upload-session/${sessionId}/cancel`), {}, 15000);
    } catch {
      /* ignore cleanup errors */
    }
    throw error;
  }
}

async function runServerSessionBinaryUpload({
  method,
  api,
  fileName,
  mimeType,
  title,
  description,
  privacyStatus,
  isShort,
  account_key,
  uploadAllChunks,
}) {
  await wakeUploadBackend(api, method);

  const startUrl = buildApiUrl(api, '/api/youtube/upload-session/start');
  logYoutubeUploadPhase(method, 'session-start', {
    startUrl,
    fileName,
    mimeType,
    chunkBytes: getChunkBytes(),
    transport: 'fetch-binary-session',
  });

  let startRes;
  try {
    startRes = await postJsonWithRetry(startUrl, { mimeType, fileName }, 60000);
  } catch (err) {
    logYoutubeUploadError(method, 'session-start', err, { startUrl });
    throw toUploadError(
      err,
      'Could not reach the server to start the video upload. Check your connection and try again.'
    );
  }

  const startData = await parseJsonText(startRes.text);
  if (startRes.status < 200 || startRes.status >= 300 || !startData.sessionId) {
    throw new Error(
      formatYoutubeUploadError(startData, 'Could not start video upload', startRes.status)
    );
  }

  const { sessionId } = startData;
  logYoutubeUploadPhase(method, 'session-started', { sessionId });

  try {
    await uploadAllChunks(async (chunkBuffer, offset, chunkIndex) => {
      const chunkUrl = buildApiUrl(api, `/api/youtube/upload-session/${sessionId}/chunk`);
      let chunkRes;
      try {
        if (chunkIndex === 1 || chunkIndex % 10 === 0) {
          logYoutubeUploadPhase(method, 'session-chunk-binary', {
            chunkIndex,
            offset,
            chunkBytes: chunkBuffer?.byteLength ?? chunkBuffer?.length,
            chunkUrl,
          });
        }
        chunkRes = await postBinaryWithRetry(chunkUrl, offset, chunkBuffer, CHUNK_UPLOAD_TIMEOUT_MS);
      } catch (err) {
        logYoutubeUploadError(method, 'session-chunk', err, {
          chunkUrl,
          offset,
          chunkIndex,
          chunkBytes: chunkBuffer?.byteLength ?? chunkBuffer?.length,
        });
        throw toUploadError(
          err,
          'Lost connection while uploading the video. Check your network and try again.'
        );
      }
      const chunkData = await parseJsonText(chunkRes.text);
      if (chunkRes.status < 200 || chunkRes.status >= 300) {
        const err = new Error(
          formatYoutubeUploadError(chunkData, 'Video upload failed', chunkRes.status)
        );
        logYoutubeUploadError(method, 'session-chunk-http', err, {
          chunkUrl,
          offset,
          chunkIndex,
          httpStatus: chunkRes.status,
        });
        throw err;
      }
    });

    logYoutubeUploadPhase(method, 'session-complete', { sessionId });
    let completeRes;
    try {
      completeRes = await postJsonWithRetry(
        buildApiUrl(api, `/api/youtube/upload-session/${sessionId}/complete`),
        { title, description, privacyStatus, isShort, account_key },
        COMPLETE_UPLOAD_TIMEOUT_MS
      );
    } catch (err) {
      logYoutubeUploadError(method, 'session-complete', err, { sessionId });
      throw toUploadError(
        err,
        'Lost connection while finishing the video upload. Check your network and try again.'
      );
    }

    const completeData = await parseJsonText(completeRes.text);
    if (completeRes.status < 200 || completeRes.status >= 300) {
      const err = new Error(
        formatYoutubeUploadError(completeData, 'YouTube upload failed', completeRes.status)
      );
      logYoutubeUploadError(method, 'session-complete-http', err, {
        sessionId,
        httpStatus: completeRes.status,
      });
      throw err;
    }
    const y = completeData.youtubeUrl;
    if (!y) throw new Error('No YouTube URL returned from server');
    logYoutubeUploadSuccess(method, { sessionId, youtubeUrl: String(y).trim() });
    return String(y).trim();
  } catch (error) {
    try {
      await postJson(buildApiUrl(api, `/api/youtube/upload-session/${sessionId}/cancel`), {}, 15000);
    } catch {
      /* ignore cleanup errors */
    }
    throw error;
  }
}

async function uploadWebFileViaServerSession({
  webFile,
  fileName,
  mimeType,
  title,
  description = '',
  privacyStatus = 'unlisted',
  isShort = false,
  account_key = 'default',
  onProgress,
}) {
  const method = YOUTUBE_UPLOAD_METHODS.WEB_SESSION_B64;
  const api = getYoutubeUploadJsonApiBase();
  const totalHint = webFile.size > 0 ? webFile.size : undefined;

  logYoutubeUploadStart(method, {
    fileName,
    mimeType,
    fileSize: formatBytes(totalHint),
    fileSizeBytes: totalHint,
    chunkBytes: getChunkBytes(),
    apiBase: api,
    transport: 'fetch-json-b64',
  });

  let chunkIndex = 0;
  try {
    const youtubeUrl = await runServerSessionUpload({
      method,
      api,
      fileName,
      mimeType,
      title,
      description,
      privacyStatus,
      isShort,
      account_key,
      uploadAllChunks: async (sendChunk) => {
        await streamFileInChunks(
          webFile,
          async (chunk, offset) => {
            chunkIndex += 1;
            if (chunkIndex === 1 || chunkIndex % 10 === 0) {
              logYoutubeUploadPhase(method, 'session-chunk-progress', {
                chunkIndex,
                offset,
                chunkBytes: chunk.length,
              });
            }
            await sendChunk(uint8ArrayToBase64(chunk), offset);
          },
          {
            totalHint,
            onProgress: (loaded, total) => {
              onProgress?.({ loaded, total: total || loaded });
            },
          }
        );
      },
    });
    return youtubeUrl;
  } catch (err) {
    logYoutubeUploadError(method, 'upload-failed', err, {
      fileName,
      chunksSent: chunkIndex,
    });
    throw err;
  }
}

async function uploadWebFileViaSessionBinary({
  webFile,
  fileName,
  mimeType,
  title,
  description = '',
  privacyStatus = 'unlisted',
  isShort = false,
  account_key = 'default',
  onProgress,
}) {
  const method = YOUTUBE_UPLOAD_METHODS.WEB_MOBILE_SESSION_BINARY;
  const api = getYoutubeUploadJsonApiBase();
  const totalHint = resolveWebFileByteLength(webFile, 0) || undefined;

  logYoutubeUploadStart(method, {
    fileName,
    mimeType,
    fileSize: formatBytes(totalHint),
    fileSizeBytes: totalHint || null,
    fileSizeKnown: Boolean(totalHint),
    chunkBytes: getChunkBytes(),
    apiBase: api,
    transport: 'slice-read + fetch-binary-session',
  });

  let chunkIndex = 0;
  try {
    return await runServerSessionBinaryUpload({
      method,
      api,
      fileName,
      mimeType,
      title,
      description,
      privacyStatus,
      isShort,
      account_key,
      uploadAllChunks: async (sendChunk) => {
        await readWebFileInSliceChunks(
          webFile,
          async (chunk, offset, idx) => {
            chunkIndex = idx;
            await sendChunk(chunk, offset, idx);
          },
          {
            method,
            totalHint,
            onProgress: (loaded, total) => {
              onProgress?.({ loaded, total: total || loaded });
            },
          }
        );
      },
    });
  } catch (err) {
    logYoutubeUploadError(method, 'upload-failed', err, {
      fileName,
      chunksSent: chunkIndex,
    });
    throw err;
  }
}

async function uploadWebFileViaResumable({
  webFile,
  fileName,
  mimeType,
  fileSize,
  title,
  description = '',
  privacyStatus = 'unlisted',
  isShort = false,
  account_key = 'default',
  onProgress,
}) {
  const method = YOUTUBE_UPLOAD_METHODS.WEB_MOBILE_RESUMABLE;
  const api = getYoutubeUploadJsonApiBase();
  if (!api) {
    throw new Error('Video upload API URL is not configured.');
  }

  const totalBytes = resolveWebFileByteLength(webFile, fileSize);
  if (!totalBytes) {
    throw new Error('Could not determine video file size. Please choose the file again.');
  }

  const tooLarge = videoTooLargeMessage(totalBytes);
  if (tooLarge) throw new Error(tooLarge);

  logYoutubeUploadStart(method, {
    fileName,
    mimeType,
    fileSize: formatBytes(totalBytes),
    fileSizeBytes: totalBytes,
    apiBase: api,
    chunkApiBase: getYoutubeUploadChunkApiBase(),
    transport: 'upload-init + slice-read + fetch-binary-resumable',
  });

  await wakeUploadBackend(api, method);

  const initUrl = buildApiUrl(api, '/api/youtube/upload-init');
  logYoutubeUploadPhase(method, 'upload-init', { initUrl, totalBytes, mimeType });

  let initRes;
  try {
    initRes = await postJsonWithRetry(
      initUrl,
      { title, description, privacyStatus, isShort, account_key, fileSize: totalBytes, mimeType },
      60000
    );
  } catch (err) {
    logYoutubeUploadError(method, 'upload-init', err, { initUrl });
    throw toUploadError(
      err,
      'Could not reach the server to start the video upload. Check your connection and try again.'
    );
  }

  const initData = await parseJsonText(initRes.text);
  if (initRes.status < 200 || initRes.status >= 300 || !initData.uploadUrl) {
    const err = new Error(
      formatYoutubeUploadError(initData, 'Could not start video upload', initRes.status)
    );
    logYoutubeUploadError(method, 'upload-init-http', err, { initUrl, httpStatus: initRes.status });
    throw err;
  }

  logYoutubeUploadPhase(method, 'upload-init-ok', { httpStatus: initRes.status });

  let chunkIndex = 0;
  try {
    const result = await uploadWebFileToYoutubeResumable({
      method,
      file: webFile,
      totalBytes,
      mimeType,
      uploadUrl: initData.uploadUrl,
      onProgress,
    });
    logYoutubeUploadSuccess(method, {
      youtubeUrl: result.youtubeUrl,
      videoId: result.videoId,
      chunksSent: chunkIndex,
    });
    return String(result.youtubeUrl).trim();
  } catch (err) {
    logYoutubeUploadError(method, 'upload-failed', err, {
      fileName,
      chunksSent: chunkIndex,
      totalBytes,
    });
    throw err;
  }
}

async function uploadNativeVideoViaResumable({
  uri,
  fileName = 'video.mp4',
  mimeType = 'video/mp4',
  fileSize,
  title,
  description = '',
  privacyStatus = 'unlisted',
  isShort = false,
  account_key = 'default',
  onProgress,
}) {
  const method = YOUTUBE_UPLOAD_METHODS.NATIVE_RESUMABLE;
  const api = getYoutubeUploadJsonApiBase();
  if (!api) {
    const err = new Error('Video upload API URL is not configured.');
    logYoutubeUploadError(method, 'config', err, { jsonApiBase: api });
    throw err;
  }

  logYoutubeUploadStart(method, {
    fileName,
    mimeType,
    fileSizeHint: formatBytes(fileSize),
    fileSizeHintBytes: fileSize,
    sourceUri: safeUriHint(uri),
    apiBase: api,
    transport: 'upload-init + FileSystem.uploadAsync chunks',
    chunkTransport: 'FileSystem.uploadAsync',
  });

  const { uri: readUri, cleanup } = await resolveNativeVideoUri(uri);
  try {
    logYoutubeUploadPhase(method, 'resolve-uri', {
      sourceUri: safeUriHint(uri),
      readUri: safeUriHint(readUri),
      copiedToCache: readUri !== uri,
    });

    const totalBytes = await getNativeVideoByteLength(readUri, fileSize);
    const tooLarge = videoTooLargeMessage(totalBytes);
    if (tooLarge) {
      const err = new Error(tooLarge);
      logYoutubeUploadError(method, 'file-too-large', err, {
        totalBytes,
        totalSize: formatBytes(totalBytes),
      });
      throw err;
    }

    logYoutubeUploadPhase(method, 'file-ready', {
      totalBytes,
      totalSize: formatBytes(totalBytes),
    });

    await wakeUploadBackend(api, method);

    const initUrl = buildApiUrl(api, '/api/youtube/upload-init');
    logYoutubeUploadPhase(method, 'upload-init', { initUrl, totalBytes, mimeType });

    let initRes;
    try {
      initRes = await postJsonWithRetry(
        initUrl,
        {
          title,
          description,
          privacyStatus,
          isShort,
          account_key,
          fileSize: totalBytes,
          mimeType,
        },
        60000
      );
    } catch (err) {
      logYoutubeUploadError(method, 'upload-init', err, { initUrl });
      throw toUploadError(
        err,
        'Could not reach the server to start the video upload. Check your connection and try again.'
      );
    }

    const initData = await parseJsonText(initRes.text);
    if (initRes.status < 200 || initRes.status >= 300 || !initData.uploadUrl) {
      const err = new Error(
        formatYoutubeUploadError(initData, 'Could not start video upload', initRes.status)
      );
      logYoutubeUploadError(method, 'upload-init-http', err, {
        initUrl,
        httpStatus: initRes.status,
      });
      throw err;
    }

    logYoutubeUploadPhase(method, 'upload-init-ok', {
      httpStatus: initRes.status,
      uploadUrlHost: (() => {
        try {
          return new URL(initData.uploadUrl).host;
        } catch {
          return '(invalid uploadUrl)';
        }
      })(),
    });

    const result = await uploadNativeUriToYoutubeResumable({
      method,
      readUri,
      totalBytes,
      mimeType,
      uploadUrl: initData.uploadUrl,
      onProgress,
    });

    const youtubeUrl = result.youtubeUrl;
    if (!youtubeUrl) {
      const err = new Error('No YouTube URL returned from server');
      logYoutubeUploadError(method, 'complete-no-url', err);
      throw err;
    }
    logYoutubeUploadSuccess(method, {
      youtubeUrl: String(youtubeUrl).trim(),
      videoId: result.videoId,
    });
    return String(youtubeUrl).trim();
  } catch (err) {
    if (!(err instanceof Error && /No YouTube URL|too large|not configured/i.test(err.message))) {
      logYoutubeUploadError(method, 'upload-failed', err, {
        fileName,
        sourceUri: safeUriHint(uri),
      });
    }
    throw err;
  } finally {
    await cleanup();
  }
}

export async function uploadYoutubeVideoFile({
  uri,
  webFile,
  webBlobUri,
  fileName = 'video.mp4',
  mimeType: mimeTypeHint,
  fileSize,
  title,
  description = '',
  privacyStatus = 'unlisted',
  isShort = false,
  account_key = 'default',
  onProgress,
}) {
  const route =
    Platform.OS !== 'web' && uri
      ? YOUTUBE_UPLOAD_METHODS.NATIVE_RESUMABLE
      : Platform.OS === 'web' && (webFile || webBlobUri)
        ? selectWebUploadMethod(webFile, fileSize)
        : 'none';
  logYoutubeUploadPhase(route, 'route-selected', {
    platform: Platform.OS,
    isMobileWeb: Platform.OS === 'web' ? isMobileWeb() : false,
    hasUri: Boolean(uri),
    hasWebFile: Boolean(webFile),
    hasWebBlobUri: Boolean(webBlobUri),
    fileName,
    webFileSize: webFile?.size,
    fileSizeHint: fileSize,
  });

  if (Platform.OS !== 'web' && uri) {
    return uploadNativeVideoViaResumable({
      uri,
      fileName,
      mimeType: mimeTypeHint || 'video/mp4',
      fileSize,
      title,
      description,
      privacyStatus,
      isShort,
      account_key,
      onProgress,
    });
  }

  if (Platform.OS === 'web' && (webFile || webBlobUri)) {
    const webMethod = selectWebUploadMethod(webFile, fileSize);
    if (webMethod === YOUTUBE_UPLOAD_METHODS.WEB_MOBILE_XHR) {
      return uploadWebFileViaMobileXHR({
        webFile,
        webBlobUri,
        fileName: webFile?.name || fileName,
        mimeType: webFile?.type || mimeTypeHint || 'video/mp4',
        title,
        description,
        privacyStatus,
        isShort,
        account_key,
        onProgress,
      });
    }
    return uploadWebFileViaServerSession({
      webFile,
      fileName: webFile.name || fileName,
      mimeType: webFile.type || mimeTypeHint || 'video/mp4',
      title,
      description,
      privacyStatus,
      isShort,
      account_key,
      onProgress,
    });
  }

  throw new Error('No video file selected.');
}
